/**
 * Portal Manta · ExtractorPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, pct } from '../../globals.js';
import { getExtractorMock } from '../extractor/getExtractorMock.jsx';
import { THl, TH, TDl, TD } from '../../app-globals.js';
import { M } from '../../tokens/colors.js';

const ExtractorPage = memo(function ExtractorPage_Inner({lote}){
  const mock=getExtractorMock(lote.sig);
  const [etapa,setEtapa]=useState(1);
  const [arquivo,setArquivo]=useState(null);
  const [instrucao,setInstrucao]=useState('');
  const [salvarRegra,setSalvarRegra]=useState(true);
  const [nomeRegra,setNomeRegra]=useState('');
  const [showPicker,setShowPicker]=useState(false);
  const [lendo,setLendo]=useState(false);
  const [logLines,setLogLines]=useState([]);
  const [readPct,setReadPct]=useState(0);

  // reset ao trocar de lote
  useEffect(()=>{ setEtapa(1);setArquivo(null);setInstrucao('');setNomeRegra('');setShowPicker(false);setLendo(false);setLogLines([]);setReadPct(0); },[lote.sig]);

  // Animação de leitura — roda após selecionar arquivo
  const iniciarLeitura=(arq)=>{
    setArquivo(arq);
    setShowPicker(false);
    setLendo(true);
    setLogLines([]);
    setReadPct(0);
    const ext=arq.tipo;
    const isPdf=ext==='PDF';
    const steps=[
      {t:0,   msg:`Abrindo arquivo: ${arq.nome}`,          pct:5},
      {t:350, msg:`Tipo detectado: ${isPdf?'PDF':'Excel'} · verificando integridade...`, pct:12},
      {t:750, msg:`Páginas/abas identificadas: ${arq.paginas}${isPdf?' páginas':' abas'}`, pct:20},
      {t:1100,msg:`Executando OCR / leitura de células...`, pct:32},
      {t:1500,msg:`Mapeando estrutura: seções, tabelas, índices`, pct:45},
      {t:1900,msg:`Localizando Anexo MEF (tipologias 1.2.x)...`, pct:58},
      {t:2250,msg:`MEF encontrado — extraindo quantidades e PR Vias`, pct:70},
      {t:2600,msg:`Identificando rodovias do SNV...`, pct:80},
      {t:2950,msg:`Checando campos obrigatórios (BDI, cronograma, praças)...`, pct:90},
      {t:3250,msg:`Leitura concluída — gerando check-list prévio`, pct:100},
    ];
    steps.forEach(({t,msg,pct},i)=>{
      setTimeout(()=>{
        setLogLines(prev=>[...prev,{msg,ok:pct===100||pct>=80}]);
        setReadPct(pct);
        if(i===steps.length-1){
          setTimeout(()=>{ setLendo(false); setEtapa(2); },600);
        }
      },t);
    });
  };

  // Quando usuário seleciona arquivo real
  const onFileChange=(e)=>{
    const f=e.target.files&&e.target.files[0];
    if(!f)return;
    const ext=f.name.split('.').pop().toUpperCase();
    const tipo=ext==='PDF'?'PDF':['XLS','XLSX'].includes(ext)?'XLS':'DOC';
    const tam=(f.size/1024/1024).toFixed(1)+' MB';
    const paginas=tipo==='PDF'?Math.round(100+Math.random()*600):null;
    iniciarLeitura({nome:f.name,tam,tipo,paginas:paginas||'—'});
    e.target.value='';
  };

  // Simular com arquivo mock do lote
  const simularComMock=()=>iniciarLeitura(mock.arquivo);

  const abrirPicker=()=>setShowPicker(true);
  const iniciarExtracao=()=>{ setEtapa(3); setTimeout(()=>setEtapa(4),1500); };
  const popularAbas=()=>setEtapa(5);
  const reiniciar=()=>{ setEtapa(1);setArquivo(null);setInstrucao('');setNomeRegra('');setShowPicker(false);setLendo(false);setLogLines([]);setReadPct(0); };

  const resumo=mock.checklist.reduce((a,c)=>{a[c.status]=(a[c.status]||0)+1;return a;},{});
  const pctCompleto=Math.round((resumo.ok||0)/mock.checklist.length*100);

  return(<div style={{padding:'14px 17px',overflowY:'auto',flex:1}}>
    {/* Header fixo */}
    <div style={{marginBottom:12}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
        <div style={{fontSize:14,fontWeight:700,color:V.br}}>Extrator ANTT</div>
        <span style={{padding:'2px 8px',background:V.blb,color:V.bl,fontSize:9,fontWeight:700,borderRadius:12}}>ANTT</span>
        <span style={{padding:'2px 8px',background:V.orp,color:V.or,fontSize:9,fontWeight:700,borderRadius:12}}>{lote.sig}</span>
      </div>
      <div style={{fontSize:10.5,color:V.g5}}>Extração de PDFs e Excel ANTT no formato canônico Manta · popula 5.1 Compara Paramétrico e 5.2 Paramétrico Manta</div>
    </div>

    {/* Stepper */}
    <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:0}}>
      {[
        {n:1,l:'Upload'},
        {n:2,l:'Check-list prévio'},
        {n:3,l:'Extração'},
        {n:4,l:'Preview canônico'},
        {n:5,l:'Popular abas'},
      ].map((s,i,arr)=>{
        const done=etapa>s.n;
        const active=etapa===s.n;
        return(<Fragment key={s.n}>
          <div style={{display:'flex',alignItems:'center',gap:7}}>
            <div style={{width:24,height:24,borderRadius:'50%',background:done?V.gn:(active?V.or:V.g2),color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700}}>{done?'✓':s.n}</div>
            <span style={{fontSize:10.5,fontWeight:active?700:500,color:active?V.or:(done?V.gn:V.g5)}}>{s.l}</span>
          </div>
          {i<arr.length-1&&<div style={{flex:1,height:2,background:etapa>s.n?V.gn:V.g2,margin:'0 10px'}}/>}
        </Fragment>);
      })}
    </div>

    {/* INPUT FILE OCULTO */}
    <input
      type="file"
      accept=".pdf,.xls,.xlsx,.doc,.docx"
      style={{display:'none'}}
      id="extrator-file-input"
      onChange={onFileChange}
    />

    {/* MODAL PICKER */}
    {showPicker&&<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}
      onClick={e=>{if(e.target===e.currentTarget)setShowPicker(false);}}>
      <div style={{background:'#fff',borderRadius:14,width:460,boxShadow:'0 20px 60px rgba(0,0,0,0.25)',overflow:'hidden'}}>
        {/* Header modal */}
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${V.g2}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:V.br}}>Selecionar arquivo para extração</div>
            <div style={{fontSize:10,color:V.g5,marginTop:2}}>PDF (editais, projetos) · Excel (MEFs, quantitativos)</div>
          </div>
          <button onClick={()=>setShowPicker(false)} style={{background:'transparent',border:'none',fontSize:18,color:V.g4,cursor:'pointer',lineHeight:1}}>×</button>
        </div>
        {/* Opção 1: arquivo real do computador */}
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${V.g1}`}}>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:10}}>📁 Seu computador</div>
          <label htmlFor="extrator-file-input" style={{
            display:'flex',alignItems:'center',gap:12,padding:'12px 14px',
            background:V.g0,border:`2px dashed ${V.g2}`,borderRadius:8,cursor:'pointer',
            transition:'border-color .15s',
          }}
            onMouseEnter={e=>e.currentTarget.style.borderColor=V.or}
            onMouseLeave={e=>e.currentTarget.style.borderColor=V.g2}>
            <div style={{fontSize:28}}>📄</div>
            <div>
              <div style={{fontSize:11.5,fontWeight:600,color:V.br}}>Clique para selecionar arquivo</div>
              <div style={{fontSize:10,color:V.g5,marginTop:2}}>Ou arraste um arquivo PDF ou Excel aqui</div>
            </div>
          </label>
        </div>
        {/* Opção 2: simular com mock do lote */}
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${V.g1}`}}>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>⚡ Simulação rápida — {lote.sig}</div>
          <div onClick={simularComMock} style={{
            display:'flex',alignItems:'center',gap:12,padding:'10px 14px',
            background:V.orp,border:`1px solid ${V.orm}`,borderRadius:8,cursor:'pointer',
            transition:'background .15s',
          }}
            onMouseEnter={e=>e.currentTarget.style.background='#fed7aa'}
            onMouseLeave={e=>e.currentTarget.style.background=V.orp}>
            <div style={{width:34,height:42,background:V.or,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:'#fff',flexShrink:0}}>{mock.arquivo.tipo}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>{mock.arquivo.nome}</div>
              <div style={{fontSize:9.5,color:V.g5,marginTop:2}}>{mock.arquivo.paginas} páginas · {mock.arquivo.tam} · Edital ANTT · {lote.sig}</div>
            </div>
            <span style={{fontSize:10,color:V.or,fontWeight:700,flexShrink:0}}>Usar →</span>
          </div>
        </div>
        {/* Opção 3: SharePoint */}
        <div style={{padding:'12px 18px'}}>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>☁ SharePoint</div>
          <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:V.blb,border:`1px solid ${V.g2}`,borderRadius:7}}>
            <span style={{fontSize:14}}>📂</span>
            <span style={{fontSize:10.5,color:V.bl,fontWeight:600,fontFamily:'monospace'}}>/Editais/ANTT/PR/</span>
            <span style={{fontSize:9,color:V.g5,marginLeft:'auto'}}>· Integração disponível em produção</span>
          </div>
        </div>
      </div>
    </div>}

    {/* LENDO ARQUIVO — painel animado */}
    {lendo&&arquivo&&<div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden'}}>
      {/* Arquivo */}
      <div style={{padding:'10px 14px',background:V.g0,borderBottom:`1px solid ${V.g2}`,display:'flex',alignItems:'center',gap:10}}>
        <div style={{width:30,height:38,background:V.orp,border:`1px solid ${V.orm}`,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:V.or,flexShrink:0}}>{arquivo.tipo}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>{arquivo.nome}</div>
          <div style={{fontSize:9.5,color:V.g5}}>{arquivo.paginas} páginas · {arquivo.tam}</div>
        </div>
        <div style={{display:'inline-block',width:18,height:18,border:`2px solid ${V.g2}`,borderTopColor:V.or,borderRadius:'50%',animation:'spin 0.8s linear infinite',flexShrink:0}}/>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
      {/* Barra de progresso */}
      <div style={{height:4,background:V.g1}}>
        <div style={{height:'100%',width:`${readPct}%`,background:V.or,transition:'width 0.4s ease',borderRadius:2}}/>
      </div>
      {/* Terminal de leitura */}
      <div style={{padding:'12px 14px',background:'#0f172a',minHeight:160,fontFamily:'monospace'}}>
        <div style={{display:'flex',gap:5,marginBottom:10}}>
          {['#ff5f56','#ffbd2e','#27c93f'].map(c=><div key={c} style={{width:9,height:9,borderRadius:'50%',background:c}}/>)}
          <span style={{fontSize:9,color:'#64748b',marginLeft:5}}>manta · extrator · leitura</span>
          <span style={{fontSize:9,color:'#f97316',marginLeft:'auto',fontWeight:700}}>{readPct}%</span>
        </div>
        {logLines.map((l,i)=>(
          <div key={i} style={{
            display:'flex',gap:6,padding:'1.5px 0',
            opacity:1,
            animation:'fadeIn 0.25s ease',
          }}>
            <span style={{color:l.ok?'#22c55e':'#38bdf8',fontSize:10,minWidth:12}}>{l.ok?'✓':'›'}</span>
            <span style={{color:l.ok?'#86efac':'#7dd3fc',fontSize:10}}>{l.msg}</span>
          </div>
        ))}
        {readPct<100&&<span style={{color:'#f97316',fontSize:10,fontFamily:'monospace'}}>█</span>}
        <style>{`@keyframes fadeIn{from{opacity:0;transform:translateX(-4px)}to{opacity:1;transform:none}}`}</style>
      </div>
    </div>}

    {/* ETAPA 1 — UPLOAD */}
    {!lendo&&etapa===1&&<div
      style={{background:'#fff',border:`2px dashed ${V.g2}`,borderRadius:10,padding:'40px 20px',textAlign:'center'}}
      onDragOver={e=>e.preventDefault()}
      onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const el=document.getElementById('extrator-file-input');if(el){Object.defineProperty(el,'files',{value:e.dataTransfer.files,writable:true});onFileChange({target:{files:e.dataTransfer.files}});}}}}
    >
      <div style={{fontSize:32,color:V.g3,marginBottom:8}}>↯</div>
      <div style={{fontSize:13,color:V.br,fontWeight:700,marginBottom:4}}>Arraste o edital ANTT, MEF ou projeto aqui</div>
      <div style={{fontSize:10.5,color:V.g5,marginBottom:16}}>Aceita PDF (editais, projetos, laudos) e Excel (MEFs, quantitativos)</div>
      <button onClick={abrirPicker} style={{padding:'10px 24px',background:V.or,color:'#fff',border:'none',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Selecionar arquivo</button>
      <div style={{marginTop:14,fontSize:9.5,color:V.g4}}>Ou sincronizar do SharePoint: <span style={{color:V.bl,fontWeight:600,cursor:'pointer'}}>/Editais/ANTT/PR/</span></div>
    </div>}

    {/* ETAPA 2 — CHECKLIST PRÉVIO */}
    {etapa===2&&<div>
      {/* Arquivo identificado */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px',marginBottom:12,display:'flex',alignItems:'center',gap:12}}>
        <div style={{width:36,height:44,background:V.orp,border:`1px solid ${V.orm}`,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:V.or}}>{arquivo.tipo}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>{arquivo.nome}</div>
          <div style={{fontSize:9.5,color:V.g5,marginTop:2}}>{arquivo.paginas} páginas · {arquivo.tam} · Tipo detectado: <b style={{color:V.bl}}>Edital ANTT</b> (confiança 94%)</div>
        </div>
        <button onClick={reiniciar} style={{padding:'5px 10px',background:'transparent',border:`1px solid ${V.g2}`,borderRadius:5,fontSize:9.5,color:V.g5,cursor:'pointer',fontFamily:'inherit'}}>Trocar arquivo</button>
      </div>

      {/* Resumo do checklist */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden',marginBottom:12}}>
        <div style={{padding:'10px 14px',background:V.g1,borderBottom:`1px solid ${V.g2}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>Check-list prévio · Estrutura esperada Manta</div>
            <div style={{fontSize:9.5,color:V.g5,marginTop:2}}>Varredura rápida antes de extração completa · economiza processamento</div>
          </div>
          <div style={{display:'flex',gap:5}}>
            <span style={{fontSize:10,fontWeight:700,color:V.gn,background:V.gnb,padding:'3px 9px',borderRadius:10}}>✓ {resumo.ok||0} OK</span>
            <span style={{fontSize:10,fontWeight:700,color:'#D97706',background:'#FEF3C7',padding:'3px 9px',borderRadius:10}}>⚠ {resumo.warn||0} alerta</span>
            <span style={{fontSize:10,fontWeight:700,color:'#B91C1C',background:'#FEF2F2',padding:'3px 9px',borderRadius:10}}>✗ {resumo.fail||0} ausente</span>
            <span style={{fontSize:10,fontWeight:700,color:V.or,background:V.orp,padding:'3px 9px',borderRadius:10}}>{pctCompleto}% completo</span>
          </div>
        </div>
        <div>
          {mock.checklist.map((c,i)=>{
            const cfg=c.status==='ok'?{ico:'✓',cor:V.gn,bg:V.gnb}:c.status==='warn'?{ico:'⚠',cor:'#D97706',bg:'#FEF3C7'}:{ico:'✗',cor:'#B91C1C',bg:'#FEF2F2'};
            return(<div key={i} style={{padding:'8px 14px',borderBottom:i<mock.checklist.length-1?`1px solid ${V.g1}`:'none',display:'grid',gridTemplateColumns:'28px 280px 1fr',gap:10,alignItems:'center',background:i%2===0?'#fff':V.g0}}>
              <div style={{width:22,height:22,borderRadius:'50%',background:cfg.bg,color:cfg.cor,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>{cfg.ico}</div>
              <div style={{fontSize:10.5,fontWeight:600,color:V.g7}}>{c.campo}</div>
              <div style={{fontSize:10.5,color:V.g5}}>{c.valor}</div>
            </div>);
          })}
        </div>
      </div>

      {/* Instrução do operador */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px',marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:4}}>Instrução adicional para o agente (opcional)</div>
        <div style={{fontSize:9.5,color:V.g5,marginBottom:8}}>Ex.: "Use distribuição SICRO DNIT Vol. 10 para os componentes Manta", "Ignore BDI, vou preencher manualmente", "Procure composições SICRO no Anexo 7B"</div>
        <textarea
          value={instrucao}
          onChange={e=>setInstrucao(e.target.value)}
          placeholder="Digite instruções em linguagem natural..."
          rows={3}
          style={{width:'100%',padding:'8px 10px',border:`1px solid ${V.g2}`,borderRadius:6,fontSize:10.5,fontFamily:'inherit',color:V.g7,resize:'vertical',outline:'none',boxSizing:'border-box'}}
        />
        {instrucao.trim()&&<div style={{marginTop:10,padding:'10px 12px',background:V.gnb,border:`1px solid ${V.gn}`,borderRadius:7}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
            <input type="checkbox" checked={salvarRegra} onChange={e=>setSalvarRegra(e.target.checked)} style={{cursor:'pointer'}}/>
            <span style={{fontSize:10.5,fontWeight:700,color:V.gn}}>Salvar esta instrução como regra reutilizável</span>
            <span style={{fontSize:9,color:V.g5,background:'#fff',padding:'1px 6px',borderRadius:10,marginLeft:'auto'}}>→ Gestão do Conhecimento · Prompts & Regras</span>
          </div>
          {salvarRegra&&<input
            type="text"
            value={nomeRegra}
            onChange={e=>setNomeRegra(e.target.value)}
            placeholder="Nome da regra (ex.: Distribuição Manta DNIT quando edital ANTT)"
            style={{width:'100%',padding:'6px 9px',border:`1px solid ${V.g2}`,borderRadius:5,fontSize:10,fontFamily:'inherit',color:V.g7,outline:'none',boxSizing:'border-box',marginTop:4}}
          />}
        </div>}
      </div>

      {/* Ações */}
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={reiniciar} style={{padding:'9px 16px',background:'#fff',border:`1px solid ${V.g2}`,borderRadius:7,fontSize:11,color:V.g5,cursor:'pointer',fontFamily:'inherit'}}>Cancelar</button>
        <button onClick={iniciarExtracao} style={{padding:'9px 20px',background:V.or,color:'#fff',border:'none',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Confirmar & Extrair →</button>
      </div>
    </div>}

    {/* ETAPA 3 — EXTRAINDO */}
    {etapa===3&&<div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'40px 20px',textAlign:'center'}}>
      <div style={{display:'inline-block',width:28,height:28,border:`3px solid ${V.g2}`,borderTopColor:V.or,borderRadius:'50%',animation:'spin 1s linear infinite'}}></div>
      <div style={{fontSize:12,color:V.br,fontWeight:700,marginTop:12}}>Extraindo dados no formato canônico Manta...</div>
      <div style={{fontSize:10.5,color:V.g5,marginTop:4}}>Processando 754 páginas · mapeando para 1.2.x.x · aplicando regras aprovadas</div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>}

    {/* ETAPA 4 — PREVIEW LADO A LADO */}
    {etapa===4&&<div>
      {/* 5.1 Compara Paramétrico */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden',marginBottom:14}}>
        <div style={{padding:'10px 14px',background:V.orp,borderBottom:`1px solid ${V.g2}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:11.5,fontWeight:700,color:V.br}}>5.1 Compara Paramétrico — {mock.extracao.length} itens extraídos</div>
            <div style={{fontSize:9.5,color:V.g5,marginTop:2}}>Quantidades e PR Vias mapeadas · formato canônico Manta (compara_parametrico.itens[])</div>
          </div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:V.g1}}>
            <th style={{...THl,width:78}}>Código</th>
            <th style={THl}>Descrição</th>
            <th style={{...TH,width:50}}>Unid.</th>
            <th style={{...TH,width:90}}>Qtde</th>
            <th style={{...TH,width:130}}>PR Vias Total</th>
            <th style={{...THl,width:180}}>Origem</th>
            <th style={{...TH,width:80}}>Confiança</th>
          </tr></thead>
          <tbody>{mock.extracao.map((r,i)=>{
            const confCfg={alta:{bg:V.gnb,c:V.gn},'média':{bg:'#FEF3C7',c:'#D97706'},baixa:{bg:'#FEF2F2',c:'#B91C1C'}};
            const cfg=confCfg[r.confianca]||{bg:V.g1,c:V.g5};
            return(<tr key={i} style={{borderBottom:`1px solid ${V.g1}`,background:i%2===0?'#fff':V.g0}}>
              <td style={{...TDl,padding:'5px 10px',fontFamily:'monospace',fontSize:9.5,color:V.g4}}>{r.cod}</td>
              <td style={{...TDl,padding:'5px 10px',fontSize:10.5,color:V.g7,fontWeight:600}}>{r.desc}</td>
              <td style={{...TD,padding:'5px 10px',fontSize:10,color:V.g5,textAlign:'center'}}>{r.unid}</td>
              <td style={{...TD,padding:'5px 10px',fontSize:10.5,fontFamily:'monospace',color:V.br,fontWeight:700}}>{r.qtd!=null?r.qtd.toLocaleString('pt-BR',{maximumFractionDigits:2}):'—'}</td>
              <td style={{...TD,padding:'5px 10px',fontSize:10.5,fontFamily:'monospace',color:V.bl}}>{r.prVias!=null?'R$ '+(r.prVias/1e6).toFixed(2).replace('.',',')+' M':'—'}</td>
              <td style={{...TDl,padding:'5px 10px',fontSize:9,color:V.g5,fontStyle:'italic'}}>{r.origem}</td>
              <td style={{...TD,padding:'5px 10px'}}><span style={{fontSize:9,fontWeight:700,color:cfg.c,background:cfg.bg,padding:'2px 8px',borderRadius:10}}>{r.confianca}</span></td>
            </tr>);
          })}</tbody>
        </table>
      </div>

      {/* 5.2 Paramétrico Manta */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden',marginBottom:14}}>
        <div style={{padding:'10px 14px',background:V.blb,borderBottom:`1px solid ${V.g2}`}}>
          <div style={{fontSize:11.5,fontWeight:700,color:V.bl}}>5.2 Paramétrico Manta — {mock.dfplus.length} tipologias com componentes</div>
          <div style={{fontSize:9.5,color:V.g5,marginTop:2}}>Decomposição não detectada no edital · aplicando distribuição DNIT Vol. 10 sugerida (estimado · baixa confiança)</div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:V.g1}}>
            <th style={{...THl,width:78}}>Código</th>
            <th style={THl}>Descrição</th>
            <th style={{...TH,width:55}}>Pav.</th>
            <th style={{...TH,width:55}}>Terr.</th>
            <th style={{...TH,width:55}}>Dren.</th>
            <th style={{...TH,width:55}}>Cont.</th>
            <th style={{...TH,width:55}}>Ilum.</th>
            <th style={{...TH,width:55}}>Sinal.</th>
            <th style={{...TH,width:80}}>Confiança</th>
            <th style={{...THl,width:130}}>Referência</th>
          </tr></thead>
          <tbody>{mock.dfplus.map((r,i)=>(<tr key={i} style={{borderBottom:`1px solid ${V.g1}`,background:i%2===0?'#fff':V.g0}}>
            <td style={{...TDl,padding:'5px 10px',fontFamily:'monospace',fontSize:9.5,color:V.g4}}>{r.cod}</td>
            <td style={{...TDl,padding:'5px 10px',fontSize:10.5,color:V.g7,fontWeight:600}}>{r.desc}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.pav}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.terr}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.dren}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.cont}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.ilum}</td>
            <td style={{...TD,padding:'5px 8px',fontSize:10,color:V.g5,fontFamily:'monospace'}}>{r.sinal}</td>
            <td style={{...TD,padding:'5px 8px'}}><span style={{fontSize:8.5,fontWeight:700,color:'#D97706',background:'#FEF3C7',padding:'2px 7px',borderRadius:10}}>{r.confianca}</span></td>
            <td style={{...TDl,padding:'5px 10px',fontSize:9,color:V.g5,fontStyle:'italic'}}>{r.origem}</td>
          </tr>))}</tbody>
        </table>
      </div>

      {/* Ações finais */}
      <div style={{display:'flex',gap:8,justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:10,color:V.g5}}>Revise os dados antes de popular as abas do Lote. Edição inline no formulário canônico estará disponível em produção.</div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={reiniciar} style={{padding:'9px 16px',background:'#fff',border:`1px solid ${V.g2}`,borderRadius:7,fontSize:11,color:V.g5,cursor:'pointer',fontFamily:'inherit'}}>Descartar</button>
          <button onClick={popularAbas} style={{padding:'9px 20px',background:V.gn,color:'#fff',border:'none',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>✓ Popular abas 5.1 e 5.2 do {lote.sig}</button>
        </div>
      </div>
    </div>}

    {/* ETAPA 5 — CONCLUÍDO */}
    {etapa===5&&<div style={{background:V.gnb,border:`2px solid ${V.gn}`,borderRadius:10,padding:'30px 20px',textAlign:'center'}}>
      <div style={{fontSize:36,color:V.gn,marginBottom:10}}>✓</div>
      <div style={{fontSize:14,fontWeight:700,color:V.gn,marginBottom:6}}>Extração concluída e dados incorporados ao {lote.sig}</div>
      <div style={{fontSize:10.5,color:V.g5,marginBottom:6,lineHeight:1.6}}>
        {mock.extracao.length} itens populados em <b>5.1 Compara Paramétrico</b> · {mock.dfplus.length} tipologias com componentes em <b>5.2 Paramétrico Manta</b>
      </div>
      {nomeRegra.trim()&&<div style={{fontSize:10,color:V.gn,background:'#fff',border:`1px solid ${V.gn}`,padding:'8px 12px',borderRadius:6,display:'inline-block',marginTop:6,fontWeight:600}}>
        ✓ Regra salva: "{nomeRegra}" → disponível em KB · Prompts & Regras
      </div>}
      <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'center'}}>
        <button onClick={reiniciar} style={{padding:'9px 16px',background:'#fff',border:`1px solid ${V.gn}`,borderRadius:7,fontSize:11,color:V.gn,cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>Nova extração</button>
      </div>
    </div>}
  </div>);
});

export { ExtractorPage };
export default ExtractorPage;
